import get from 'lodash/get';
import {
  BULK_EDIT_INCIDENTS_FAIL,
  BULK_EDIT_INCIDENTS_START,
  BULK_EDIT_INCIDENTS_SUCCESS,
  BULK_EDIT_BY_FILTERS_START,
  BULK_EDIT_BY_FILTERS_END,
  CLEAR_INCIDENT_SELECTION,
  FETCH_INCIDENTS_FAIL,
  FETCH_INCIDENTS_START,
  FETCH_INCIDENTS_SUCCESS,
  PAGE_INCIDENTS_FAIL,
  PAGE_INCIDENTS_START,
  PAGE_INCIDENTS_SUCCESS,
  SELECT_INCIDENT,
  SELECT_ALL_INCIDENTS,
  SORT_INCIDENTS,
  FETCH_TOTAL_NUM_INCIDENTS_START,
  FETCH_TOTAL_NUM_INCIDENTS_SUCCESS,
  INCIDENT_LIST_PAGE_SEARCH_SERVICES_START,
  INCIDENT_LIST_PAGE_SEARCH_SERVICES_SUCCESS,
  INCIDENT_LIST_PAGE_SEARCH_SERVICES_FAIL,
  INCIDENT_LIST_PAGE_ACTIVE_SERVICES_FILTERS,
  INCIDENT_LIST_PAGE_RESET_SERVICES_SEARCH,
  INCIDENT_LIST_PAGE_SET_ACTIVE_ITEMS,
  INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES,
  INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_START,
  INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_SUCCESS,
  INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_FAIL,
} from 'actions/incidentManagement/viewIncidents';
import { transformIncidents } from './utils';

const initialState = {
  count: 0,
  error: null,
  incidentList: [],
  links: {},
  loading: false,
  previouslySelectedIncidentIDs: [],
  total: 0,
  isSelectingAllIncidents: false,
  // Used to determine if we should show the loading even if we have just received incidents
  // Since bulk editing by filter will have many requests
  isBulkEditingByFilters: false,
  // The number of incidents in the current filter
  numIncidentsInFilter: 0,
  // Total number of incidents, in and out of filters
  totalNumIncidents: 0,
  impactedServices: {
    total: 0,
    data: [],
    loading: false,
    activeItems: [],
  },
  latestIncidentListResponseTimestamp: 0,
};

export default function incidents(state = initialState, action) {
  switch (action.type) {
    case SORT_INCIDENTS:
      // Placeholder. When we implement sort, fill this out.
      return state;
    case FETCH_INCIDENTS_START:
    case FETCH_TOTAL_NUM_INCIDENTS_START:
      return {
        ...initialState,
        loading: true,
      };
    case BULK_EDIT_INCIDENTS_START:
    case PAGE_INCIDENTS_START:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case BULK_EDIT_INCIDENTS_SUCCESS:
      return {
        ...state,
        loading: false,
      };
    case FETCH_INCIDENTS_SUCCESS: {
      // Invoked after component mount, or initial load after filtering. Not invoked by pagination.
      const { count, data, error, links, total, xmRequestTimestamp } =
        action.payload;

      if (state.latestIncidentListResponseTimestamp > xmRequestTimestamp) {
        return state;
      }

      const previouslySelectedIncidentIDs = state.incidentList
        .filter(incident => incident.selected)
        .map(incident => incident.id);

      return {
        ...state,
        count,
        error,
        incidentList: transformIncidents(previouslySelectedIncidentIDs, data),
        latestIncidentListResponseTimestamp: xmRequestTimestamp,
        links,
        loading: false,
        previouslySelectedIncidentIDs,
        total,
      };
    }
    case PAGE_INCIDENTS_SUCCESS: {
      // Invoked by getting more pages (possibly after filtering as well).
      const { count, data, error, links, total } = action.payload;
      return {
        ...state,
        count,
        error,
        // Similar to FETCH_INCIDENT_SUCCESS, but we want to append instead of replace.
        /**
         * TODO: future: consider using getListPageSuccessNewData or similar to place the data
         *       at locations based on the actual offset/limit instead of simply appending.
         *       This (+ changing to the newer table component) would allow it to scroll-and-load like other pages
         */
        incidentList: [
          ...state.incidentList,
          ...transformIncidents(state.previouslySelectedIncidentIDs, data),
        ],
        links,
        loading: false,
        total,
      };
    }
    case FETCH_INCIDENTS_FAIL: {
      const { xmRequestTimestamp } = action.initialPayload;
      if (state.latestIncidentListResponseTimestamp > xmRequestTimestamp) {
        // Errors suck, but hey - at least a newer request succeeded! Keep the newer response.
        return state;
      }
      return {
        ...state,
        error: get(action, 'payload.error', 'error'),
        loading: false,
      };
    }
    case BULK_EDIT_INCIDENTS_FAIL:
    case PAGE_INCIDENTS_FAIL:
      return {
        ...state,
        error: get(action, 'payload.error', 'error'),
        loading: false,
      };
    case SELECT_INCIDENT:
      if (state.isSelectingAllIncidents) {
        // Select every incident except this one
        return {
          ...state,
          incidentList: state.incidentList.map(i => ({
            ...i,
            selected: i.id !== action.payload.id,
          })),
          isSelectingAllIncidents: false,
          // Even though the user shouldn't be able to uncheck the incident when loaded, reset the load bc otherwise it could just continue loading forever
          isBulkEditingByFilters: false,
        };
      }
      return {
        ...state,
        incidentList: state.incidentList.map(incident => ({
          ...incident,
          selected:
            incident.id === action.payload.id
              ? !incident.selected
              : incident.selected,
        })),
      };
    case SELECT_ALL_INCIDENTS:
      return {
        ...state,
        isSelectingAllIncidents: true,
      };
    case CLEAR_INCIDENT_SELECTION:
      return {
        ...state,
        incidentList: state.incidentList.map(incident => ({
          ...incident,
          selected: false,
        })),
        isSelectingAllIncidents: false,
      };
    case BULK_EDIT_BY_FILTERS_START:
      return {
        ...state,
        isBulkEditingByFilters: true,
      };
    case BULK_EDIT_BY_FILTERS_END:
      return {
        ...state,
        isBulkEditingByFilters: false,
      };
    case FETCH_TOTAL_NUM_INCIDENTS_SUCCESS:
      return {
        ...state,
        totalNumIncidents: action.payload.total,
      };
    case INCIDENT_LIST_PAGE_SEARCH_SERVICES_SUCCESS: {
      const { data, total } = action.payload;
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          data,
          total,
          loading: false,
        },
      };
    }
    case INCIDENT_LIST_PAGE_SEARCH_SERVICES_START: {
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          loading: true,
        },
      };
    }
    case INCIDENT_LIST_PAGE_SEARCH_SERVICES_FAIL: {
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          loading: false,
        },
      };
    }
    case INCIDENT_LIST_PAGE_ACTIVE_SERVICES_FILTERS: {
      const { payload } = action;
      const activeItems = [
        ...state.impactedServices.activeItems,
        ...payload.filter(
          item =>
            !state.impactedServices.activeItems.find(a => a.id === item.id),
        ),
      ];
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          activeItems,
        },
      };
    }
    case INCIDENT_LIST_PAGE_SET_ACTIVE_ITEMS: {
      const { payload } = action;
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          activeItems: payload,
        },
      };
    }
    case INCIDENT_LIST_PAGE_RESET_SERVICES_SEARCH: {
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          data: [],
          total: 0,
        },
      };
    }
    case INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES: {
      return state;
    }
    case INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          data,
          loading: false,
          activeItems: data,
        },
      };
    }
    case INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_START: {
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          loading: true,
        },
      };
    }
    case INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_FAIL: {
      return {
        ...state,
        impactedServices: {
          ...state.impactedServices,
          loading: false,
        },
      };
    }

    default:
      return state;
  }
}
